"use client"

import type { MembersHomeOverview } from "@ibada/shared-types/overview"

import { Link } from "react-router-dom"

import { HomeTile, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { PersonContactButtons } from "@/features/dashboard/home-d4/components/person-contact-buttons"
import { fullName, monthKeyToDate } from "@/features/dashboard/home-d4/lib/home-format"
import { MemberAvatar } from "@/features/members/components/members-views"
import { useLocale, useTranslations } from "@/i18n"

/**
 * New this month (D4): who joined this calendar month, their first group, and how the two months before went.
 *
 * At the start of a month nobody may have joined yet. The tile then shows LAST month's newcomers, muted and
 * labelled as last month (the people still worth welcoming), the way the Finance board falls back to the
 * previous period. With nobody in either month it is one short line, never a large empty card.
 */
export function NewMembersTile({
  newMembers,
  membersLink,
  addMember,
}: {
  newMembers: MembersHomeOverview["newMembers"]
  membersLink: HomeTileLink | null
  addMember: HomeTileLink | null
}) {
  const t = useTranslations("dashboard.home.newMembers")
  const locale = useLocale()
  const dateFormat = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" })
  const monthFormat = new Intl.DateTimeFormat(locale, { month: "long" })
  const [previous, beforeThat] = newMembers.previousMonths
  const hasCurrent = newMembers.items.length > 0
  const fallbackItems = hasCurrent ? [] : (newMembers.previousMonthItems ?? [])
  const isFallback = fallbackItems.length > 0
  // Three rows leave room for the months line at the tile height.
  const people = (hasCurrent ? newMembers.items : fallbackItems).slice(0, MAX_ROWS)

  const subtitle = hasCurrent
    ? t(newMembers.thisMonthCount === 1 ? "subtitleOne" : "subtitleOther", { count: newMembers.thisMonthCount })
    : isFallback && previous
      ? t(previous.count === 1 ? "fallbackOne" : "fallbackOther", {
          month: capitalize(monthFormat.format(monthKeyToDate(previous.month))),
          count: previous.count,
          current: monthFormat.format(nextMonth(monthKeyToDate(previous.month))),
        })
      : t("subtitleNone")

  const history =
    previous && beforeThat ? (
      <span className={`text-xs text-muted-foreground ${people.length > 0 ? "mt-auto" : ""}`}>
        {t("previousMonths", {
          firstMonth: capitalize(monthFormat.format(monthKeyToDate(previous.month))),
          firstCount: previous.count,
          secondMonth: monthFormat.format(monthKeyToDate(beforeThat.month)),
          secondCount: beforeThat.count,
        })}
      </span>
    ) : null

  const addLink = addMember ? (
    <Link to={addMember.href} className='text-[12.5px] font-bold text-primary hover:underline'>
      {addMember.label}
    </Link>
  ) : null

  return (
    <HomeTile title={t("title")} subtitle={subtitle} link={membersLink} testId='home-tile-new-members'>
      {people.length === 0 ? (
        // Nobody this month or last: one compact line and the next action, at the top of the tile.
        <div data-testid='new-members-none' className='flex flex-col items-start gap-1.5'>
          <p className='text-[13px] text-muted-foreground'>{t("emptyBoth")}</p>
          {addLink}
        </div>
      ) : (
        <ul className={`flex flex-col gap-2 ${isFallback ? "opacity-60" : ""}`} data-testid={isFallback ? "new-members-fallback" : undefined}>
          {people.map((person) => {
            const name = fullName(person.firstName, person.lastName)
            const joined = t("joined", { date: dateFormat.format(new Date(person.joinedAt)) })
            return (
              <li key={person.id} data-testid='new-member-row' className='flex items-center gap-2.5'>
                <MemberAvatar member={{ name, avatarUrl: person.avatarUrl }} className='size-[30px] text-[11px]' />
                <span className='flex min-w-0 flex-1 flex-col'>
                  <span className='truncate text-[13px] font-bold'>{name}</span>
                  <span className='truncate text-[11.5px] text-muted-foreground'>
                    {person.firstGroupName ? `${joined} · ${person.firstGroupName}` : joined}
                  </span>
                </span>
                <PersonContactButtons name={name} phone={person.phone} showCall={false} />
              </li>
            )
          })}
        </ul>
      )}
      {isFallback ? (
        <span className='mt-auto flex items-baseline justify-between gap-2'>
          {history}
          {addLink}
        </span>
      ) : (
        history
      )}
    </HomeTile>
  )
}

const MAX_ROWS = 3

const capitalize = (value: string) => value.charAt(0).toLocaleUpperCase() + value.slice(1)

const nextMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 15, 12)
